import { useState, lazy, Suspense } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleGate from './components/RoleGate.jsx';
import GlobalSearch from './components/GlobalSearch.jsx';
import Login from './pages/Login.jsx';
import { useAuth } from './contexts/AuthContext.jsx';

// Страницы грузятся лениво: recharts/leaflet и тяжёлые страницы не скачиваются,
// пока пользователь на них не зашёл.
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const OpsDashboard = lazy(() => import('./pages/OpsDashboard.jsx'));
const MapView = lazy(() => import('./pages/MapView.jsx'));
const Devices = lazy(() => import('./pages/Devices.jsx'));
const DeviceDetail = lazy(() => import('./pages/DeviceDetail.jsx'));
const Modems = lazy(() => import('./pages/Modems.jsx'));
const Objects = lazy(() => import('./pages/Objects.jsx'));
const ObjectDetail = lazy(() => import('./pages/ObjectDetail.jsx'));
const Equipment = lazy(() => import('./pages/Equipment.jsx'));
const Integrations = lazy(() => import('./pages/Integrations.jsx'));
const SiteTemplates = lazy(() => import('./pages/SiteTemplates.jsx'));
const MikrotikCommands = lazy(() => import('./pages/MikrotikCommands.jsx'));
const Subscriptions = lazy(() => import('./pages/Subscriptions.jsx'));
const NetworkOverview = lazy(() => import('./pages/NetworkOverview.jsx'));
const Discovery = lazy(() => import('./pages/Discovery.jsx'));
const Alerts = lazy(() => import('./pages/Alerts.jsx'));
const Rules = lazy(() => import('./pages/Rules.jsx'));
const Channels = lazy(() => import('./pages/Channels.jsx'));
const Users = lazy(() => import('./pages/Users.jsx'));
const VideoVehicles = lazy(() => import('./pages/VideoVehicles.jsx'));
const VehicleTrack = lazy(() => import('./pages/VehicleTrack.jsx'));
const SiteVideo = lazy(() => import('./pages/SiteVideo.jsx'));
const Coverage = lazy(() => import('./pages/Coverage.jsx'));
const AutographLinks = lazy(() => import('./pages/AutographLinks.jsx'));
const TrassirJournal = lazy(() => import('./pages/TrassirJournal.jsx'));
const PhotoMap = lazy(() => import('./pages/PhotoMap.jsx'));

const pageFallback = <div className="muted" style={{ padding: 16 }}>Загрузка…</div>;

function Layout({ children }) {
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);
  // Встроенный режим (?embed=1) — для вставки страницы в minibrowser Trassir:
  // без бокового меню/шапки/поиска, контент на весь экран.
  const embed = new URLSearchParams(useLocation().search).get('embed') === '1';
  if (embed) return <main className="app-main embed-main"><Suspense fallback={pageFallback}>{children}</Suspense></main>;
  return (
    <div className="app-shell">
      {/* Мобильная верхняя панель с «бургером» — видна только на узких экранах */}
      <header className="mobile-topbar">
        <button className="hamburger" aria-label="Меню" onClick={() => setDrawerOpen((v) => !v)}>☰</button>
        <span className="mobile-brand">⚡ intek monitoring</span>
      </header>
      {drawerOpen && <div className="sidebar-backdrop" onClick={closeDrawer} />}
      <aside className={`sidebar${drawerOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">⚡ intek monitoring</div>
        <nav onClick={closeDrawer}>
          <div className="sidebar-section">Обзор</div>
          <NavLink to="/" end>📊 Главная</NavLink>
          <NavLink to="/ops">🚨 Дежурная</NavLink>
          <NavLink to="/map">🗺 Карта</NavLink>
          <NavLink to="/vehicle-track">🛰 Трек ТС</NavLink>
          <NavLink to="/network">🔌 Топология</NavLink>
          <NavLink to="/coverage">📶 Карта покрытия</NavLink>
          <NavLink to="/video-vehicles">🎥 Видео на ТС</NavLink>
          <NavLink to="/site-video">🏢 Видео на объектах</NavLink>
          <NavLink to="/photos">📸 Фотофиксация</NavLink>
          <RoleGate roles={['admin', 'manager']}>
            <NavLink to="/autograph-links">🔗 AG-привязка</NavLink>
            <NavLink to="/trassir-journal">🕵️ Журнал Трассира</NavLink>
          </RoleGate>

          <div className="sidebar-section">Объекты и устройства</div>
          <NavLink to="/objects">🏢 Объекты</NavLink>
          <NavLink to="/devices">⚙ Устройства</NavLink>
          <NavLink to="/modems">📡 Модемы</NavLink>

          <div className="sidebar-section">Алерты</div>
          <NavLink to="/alerts">🔔 События</NavLink>
          <NavLink to="/rules">📐 Правила</NavLink>
          <RoleGate roles={['admin', 'manager']}>
            <NavLink to="/channels">📨 Каналы</NavLink>
          </RoleGate>

          <div className="sidebar-section">Учёт</div>
          <NavLink to="/subscriptions">📋 Подписки/SIM</NavLink>
          <NavLink to="/equipment">🔧 Оборудование</NavLink>

          <RoleGate roles={['admin', 'manager']}>
            <div className="sidebar-section">Инструменты</div>
            <NavLink to="/discovery">🔎 Discovery</NavLink>
            <NavLink to="/site-templates">🏗 Шаблоны</NavLink>
            <NavLink to="/mikrotik-cmd">💻 Команды MT</NavLink>
          </RoleGate>

          <RoleGate roles={['admin']}>
            <div className="sidebar-section">Администрирование</div>
            <NavLink to="/integrations">🔗 Интеграции</NavLink>
            <NavLink to="/users">👥 Пользователи</NavLink>
          </RoleGate>
        </nav>
        <div className="sidebar-user">
          <div className="sidebar-search-hint" title="Глобальный поиск">
            <kbd>Ctrl</kbd>+<kbd>K</kbd> поиск
          </div>
          <div><strong>{user?.email}</strong></div>
          <div className="muted">{user?.role}</div>
          <button onClick={logout}>Выйти</button>
        </div>
      </aside>
      <main className="app-main"><Suspense fallback={pageFallback}>{children}</Suspense></main>
      <GlobalSearch />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/ops" element={<ProtectedRoute><Layout><OpsDashboard /></Layout></ProtectedRoute>} />
      <Route path="/map" element={<ProtectedRoute><Layout><MapView /></Layout></ProtectedRoute>} />
      <Route path="/vehicle-track" element={<ProtectedRoute><Layout><VehicleTrack /></Layout></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><Layout><Devices /></Layout></ProtectedRoute>} />
      <Route path="/modems" element={<ProtectedRoute><Layout><Modems /></Layout></ProtectedRoute>} />
      <Route path="/devices/:id" element={<ProtectedRoute><Layout><DeviceDetail /></Layout></ProtectedRoute>} />
      <Route path="/objects" element={<ProtectedRoute><Layout><Objects /></Layout></ProtectedRoute>} />
      <Route path="/objects/:id" element={<ProtectedRoute><Layout><ObjectDetail /></Layout></ProtectedRoute>} />
      <Route path="/subscriptions" element={<ProtectedRoute><Layout><Subscriptions /></Layout></ProtectedRoute>} />
      <Route path="/network" element={<ProtectedRoute><Layout><NetworkOverview /></Layout></ProtectedRoute>} />
      <Route path="/coverage" element={<ProtectedRoute><Layout><Coverage /></Layout></ProtectedRoute>} />
      <Route path="/equipment" element={<ProtectedRoute><Layout><Equipment /></Layout></ProtectedRoute>} />
      <Route path="/site-templates" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><SiteTemplates /></Layout></ProtectedRoute>} />
      <Route path="/discovery" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><Discovery /></Layout></ProtectedRoute>} />
      <Route path="/mikrotik-cmd" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><MikrotikCommands /></Layout></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Layout><Alerts /></Layout></ProtectedRoute>} />
      <Route path="/rules" element={<ProtectedRoute><Layout><Rules /></Layout></ProtectedRoute>} />
      <Route path="/channels" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><Channels /></Layout></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute roles={['admin']}><Layout><Integrations /></Layout></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute roles={['admin']}><Layout><Users /></Layout></ProtectedRoute>} />
      <Route path="/video-vehicles" element={<ProtectedRoute><Layout><VideoVehicles /></Layout></ProtectedRoute>} />
      <Route path="/site-video" element={<ProtectedRoute><Layout><SiteVideo /></Layout></ProtectedRoute>} />
      <Route path="/photos" element={<ProtectedRoute><Layout><PhotoMap /></Layout></ProtectedRoute>} />
      <Route path="/autograph-links" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><AutographLinks /></Layout></ProtectedRoute>} />
      <Route path="/trassir-journal" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><TrassirJournal /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
